import { RAID_BRIDGE_ENDPOINT } from '../api/raidBridgeApi.js';
import { RAID_BRIDGE_USERSCRIPT_URL, RAID_BRIDGE_USERSCRIPT_VERSION } from '../api/raidBridgeInstaller.js';
import { useRaidBridge } from '../hooks/useRaidBridge.js';
import { RaidBridgeDirectSync } from '../shared/RaidBridgeDirectSync.jsx';
import { RaidBridgeQuickStart } from '../shared/RaidBridgeQuickStart.jsx';

const MODE_LABELS = { snapshot: '전체 갱신', incremental: '새 기록' };

function formatDateTime(value) {
  if (!value) return '없음';
  try { return new Intl.DateTimeFormat('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(value)); }
  catch { return '확인 불가'; }
}

export function DesktopRaidBridge() {
  const bridge = useRaidBridge();
  if (!bridge.loading && !bridge.canManage && !bridge.unionId) return null;
  if (!bridge.loading && !bridge.canManage) return null;

  const ready = Boolean(bridge.collectorReady);
  const installerText = bridge.installer.checking
    ? '확인 중'
    : bridge.installer.installed
      ? `설치됨${bridge.installer.version ? ` · v${bridge.installer.version}` : ''}`
      : '미설치';
  const revoke = () => {
    if (window.confirm('현재 유니온 공용 토큰을 해제할까요? 모든 관리자 PC의 수집이 중단됩니다.')) bridge.revoke();
  };
  const statusLabel = bridge.tokenOutdated ? '구형 토큰' : ready ? '수집 가능' : '설정 필요';

  return (
    <section className="desktop-raid-bridge" aria-label="레이드 수집기 관리">
      <header className="desktop-raid-bridge-heading">
        <div>
          <h2>{bridge.unionName || '유니온'} 레이드 수집기</h2>
          <p>BlablaLink의 하드 유니온 레이드 실전 기록, 보스 정보, 현재 멤버 명단을 DoroParty에 반영합니다.</p>
        </div>
        <strong className={ready ? 'ready' : 'setup'}>{statusLabel}</strong>
      </header>

      {bridge.loading ? (
        <div className="desktop-raid-bridge-loading" role="status"><span /><p>수집기 상태를 확인하고 있습니다.</p></div>
      ) : (
        <>
          <RaidBridgeQuickStart bridge={bridge} />
          <RaidBridgeDirectSync bridge={bridge} />

          <section className="desktop-raid-bridge-history" aria-label="최근 수집 기록">
            <div className="raid-bridge-section-header">
              <div><h3>최근 수집 기록</h3><p>최근 실행 결과를 시간순으로 확인합니다.</p></div>
            </div>
            {bridge.recentBatches.length === 0 ? (
              <p className="empty">아직 수집한 기록이 없습니다.</p>
            ) : (
              <div className="desktop-raid-bridge-history-table-wrap">
                <table>
                  <thead><tr><th>수집 시각</th><th>차수</th><th>방식</th><th>받은 기록</th><th>반영</th><th>정리</th></tr></thead>
                  <tbody>{bridge.recentBatches.slice(0, 10).map((batch) => (
                    <tr key={batch.id}>
                      <td>{formatDateTime(batch.createdAt)}</td>
                      <td>{batch.seasonNo}차</td>
                      <td>{MODE_LABELS[batch.mode] || batch.mode}</td>
                      <td>{batch.recordCount}</td>
                      <td>{batch.upsertedCount}</td>
                      <td>{batch.removedCount}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </section>

          <details className="desktop-raid-bridge-advanced">
            <summary><span>고급 설정</span><small>수신 주소, 설치 파일, 공용 토큰 전체 해제</small></summary>
            <div className="desktop-raid-installer">
              <div className="desktop-raid-installer-head">
                <div><strong>Tampermonkey 연결 관리</strong><p>수집기 연결 정보 확인이나 전체 연결 해제가 필요할 때만 사용합니다.</p></div>
                <div className={`desktop-raid-installer-state ${bridge.installer.installed ? 'installed' : 'missing'}`}><strong>{installerText}</strong><small>필요 버전 v{RAID_BRIDGE_USERSCRIPT_VERSION}</small></div>
              </div>
              <div className="desktop-raid-installer-path"><span>수신 주소</span><code>{RAID_BRIDGE_ENDPOINT}</code></div>
              <div className="desktop-raid-installer-path"><span>설치 파일</span><code>{RAID_BRIDGE_USERSCRIPT_URL}</code></div>
              {bridge.activeToken ? <div className="desktop-raid-bridge-actions"><button className="revoke" type="button" disabled={Boolean(bridge.working)} onClick={revoke}>{bridge.working === 'revoke' ? '해제 중…' : '공용 토큰 전체 해제'}</button></div> : null}
            </div>
          </details>
        </>
      )}

      {bridge.notice.message ? <p className={`desktop-raid-bridge-notice ${bridge.notice.type}`} role="status">{bridge.notice.message}</p> : null}
    </section>
  );
}
